import * as React from 'react';
import { clsx } from 'clsx';
import { Info, AlertCircle, AlertTriangle, CheckCircle, AlertOctagon } from 'lucide-react';

export interface ToastProps {
  /**
   * The variant of the toast
   */
  variant: 'information' | 'danger' | 'warning' | 'success' | 'caution';
  /**
   * The message to display
   */
  message: string;
  /**
   * Callback when close button is clicked
   */
  onClose?: () => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const baseToastStyles: React.CSSProperties = {
  width: '360px',
  padding: '16px',
  borderRadius: '8px',
  border: '1px solid',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  boxShadow: '0px 4px 12px 0px rgba(32, 32, 32, 0.08)',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const messageStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  flex: 1,
};

const closeButtonStyles: React.CSSProperties = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'background-color 0.2s ease-in-out',
};

const variantConfig = {
  information: {
    backgroundColor: '#f5fbff',
    borderColor: '#2c8bca',
    textColor: '#053a61',
    icon: Info,
    iconColor: '#2c8bca',
    closeHoverBg: '#e0f3ff',
  },
  danger: {
    backgroundColor: '#fff5f5',
    borderColor: '#c93232',
    textColor: '#610202',
    icon: AlertCircle,
    iconColor: '#c93232',
    closeHoverBg: '#ffe0e0',
  },
  warning: {
    backgroundColor: '#fffaf5',
    borderColor: '#e4720d',
    textColor: '#611f00',
    icon: AlertTriangle,
    iconColor: '#e4720d',
    closeHoverBg: '#ffedd5',
  },
  success: {
    backgroundColor: '#f0faf3',
    borderColor: '#5ec171',
    textColor: '#024002',
    icon: CheckCircle,
    iconColor: '#5ec171',
    closeHoverBg: '#d9f2df',
  },
  caution: {
    backgroundColor: '#fffdf5',
    borderColor: '#edae06',
    textColor: '#613f00',
    icon: AlertOctagon,
    iconColor: '#edae06',
    closeHoverBg: '#fff8d5',
  },
};

/**
 * Toast component - Arbor Design System
 *
 * A notification toast with different variants for different message types.
 *
 * IMPORTANT: Toasts are designed to overlay content in the top-right corner.
 * Use absolute/fixed positioning to display them above page content.
 *
 * For inline notifications that flow with page content, use the Banner component instead.
 *
 * Example usage with positioning:
 * ```tsx
 * <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 1000 }}>
 *   <Toast variant="success" message="Changes saved!" onClose={() => {}} />
 * </div>
 * ```
 */
export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      variant,
      message,
      onClose,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [isCloseHovered, setIsCloseHovered] = React.useState(false);
    const config = variantConfig[variant];
    const IconComponent = config.icon;

    const toastStyle: React.CSSProperties = {
      ...baseToastStyles,
      backgroundColor: config.backgroundColor,
      borderColor: config.borderColor,
      ...style,
    };

    const closeStyle: React.CSSProperties = {
      ...closeButtonStyles,
      ...(isCloseHovered && { backgroundColor: config.closeHoverBg }),
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-toast', `arbor-toast-${variant}`, className)}
        style={toastStyle}
        data-testid={dataTestId}
        role="alert"
        aria-live="polite"
      >
        <IconComponent size={16} color={config.iconColor} style={{ flexShrink: 0 }} />
        <span style={{ ...messageStyles, color: config.textColor }}>
          {message}
        </span>
        {onClose && (
          <button
            onClick={onClose}
            onMouseEnter={() => setIsCloseHovered(true)}
            onMouseLeave={() => setIsCloseHovered(false)}
            style={closeStyle}
            aria-label="Close notification"
            type="button"
          >
            <span style={{ fontSize: '18px', lineHeight: 1, color: '#595959', fontWeight: 'bold' }}>×</span>
          </button>
        )}
      </div>
    );
  }
);

Toast.displayName = 'Toast';
